import { useEffect, useMemo, useState } from "react";
import { api } from "./api";

function cn(...xs) {
    return xs.filter(Boolean).join(" ");
}

function Dot({ card }) {
    const cls =
        card === "green"
            ? "bg-green-600"
            : card === "yellow"
                ? "bg-yellow-400"
                : "bg-red-600";
    return <span className={cn("inline-block w-3 h-3 rounded-full", cls)} />;
}

function Badge({ children, tone = "gray" }) {
    const cls =
        tone === "blue"
            ? "bg-blue-50 text-blue-700 border-blue-100"
            : tone === "purple"
                ? "bg-purple-50 text-purple-700 border-purple-100"
                : "bg-gray-50 text-gray-700 border-gray-100";
    return (
        <span className={cn("px-2 py-1 text-xs rounded-full border", cls)}>
            {children}
        </span>
    );
}

function BottomSheet({ open, title, children, onClose }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-xl p-4 max-h-[85vh] overflow-auto">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="font-semibold text-base">{title}</div>
                    <button
                        onClick={onClose}
                        className="px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm"
                    >
                        Fermer
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function CardToggle({ value, onChange }) {
    const items = [
        { key: "green", label: "Vert", cls: "bg-green-600 text-white" },
        { key: "yellow", label: "Jaune", cls: "bg-yellow-400 text-black" },
        { key: "red", label: "Rouge", cls: "bg-red-600 text-white" },
    ];

    return (
        <div className="flex gap-2">
            {items.map((it) => (
                <button
                    key={it.key}
                    onClick={() => onChange(it.key)}
                    className={cn(
                        "flex-1 py-2 rounded-xl text-sm font-semibold border transition active:scale-[0.98]",
                        value === it.key
                            ? cn(it.cls, "border-transparent")
                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    )}
                >
                    {it.label}
                </button>
            ))}
        </div>
    );
}

export default function Students() {
    const [students, setStudents] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [draftComment, setDraftComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [savedComment, setSavedComment] = useState("");
    const [sortMode, setSortMode] = useState("alpha"); // "alpha" ou "no-roles"

    const selected = useMemo(
        () => students.find((s) => s.id === openId) || null,
        [students, openId]
    );

    const sortedStudents = useMemo(() => {
        const sorted = [...students];

        if (sortMode === "no-roles") {
            // Tri : ceux sans rôle en haut, puis alphabétique
            sorted.sort((a, b) => {
                const aHasNoRole = !a.role_front && !a.role_back;
                const bHasNoRole = !b.role_front && !b.role_back;

                if (aHasNoRole && !bHasNoRole) return -1;
                if (!aHasNoRole && bHasNoRole) return 1;

                // Sinon tri alphabétique
                return a.name.localeCompare(b.name, 'fr');
            });
        } else {
            // Tri alphabétique par défaut
            sorted.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        }

        return sorted;
    }, [students, sortMode]);

    async function load() {
        const list = await api.listStudents();
        setStudents(list);
    }


    useEffect(() => {
        load();
    }, []);

    async function update(id, patch) {
        await api.updateStudent({ id, ...patch });
        await load(); // simple et robuste (3-4 users max → OK)
    }


    async function reset(type) {
        if (!confirm("Confirmer la réinitialisation ?")) return;

        console.log("RESET CLICK", type, "origin", location.origin);
        const me = await api.me();

        await api.reset(type);
        await load();
    }


    function openDetails(s) {
        setOpenId(s.id);
        setDraftComment(s.comment || "");
        setSavedComment(s.comment || "");
    }

    async function saveComment() {
        if (!selected) return;
        setSaving(true);
        try {
            await update(selected.id, { comment: draftComment });
            setOpenId(null);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        if (!selected) return;
        if (draftComment === savedComment) return;

        const t = setTimeout(async () => {
            try {
                setSaving(true);
                await update(selected.id, { comment: draftComment });
                setSavedComment(draftComment);
            } finally {
                setSaving(false);
            }
        }, 700); // délai confortable mobile

        return () => clearTimeout(t);
    }, [draftComment, selected?.id]);


    async function toggleRole(id, field, current) {
        await update(id, { [field]: current ? 0 : 1 });
    }

    return (
        <div className="min-h-screen">
            {/* Header actions */}
            <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
                <div className="max-w-xl mx-auto p-3 flex gap-2 overflow-x-auto">
                    <button
                        onClick={() => reset("cards")}
                        className="px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap"
                    >
                        Reset cartons
                    </button>
                    <button
                        onClick={() => reset("roles")}
                        className="px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap"
                    >
                        Reset emplacements
                    </button>
                    <button
                        onClick={() => reset("comments")}
                        className="px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap"
                    >
                        Reset commentaires
                    </button>
                </div>
                <div className="max-w-xl mx-auto px-3 pb-3 flex gap-2">
                    <button
                        onClick={() => setSortMode("alpha")}
                        className={cn(
                            "px-3 py-2 rounded-full text-sm whitespace-nowrap transition",
                            sortMode === "alpha"
                                ? "bg-gray-900 text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                        )}
                    >
                        Tri alphabétique
                    </button>
                    <button
                        onClick={() => setSortMode("no-roles")}
                        className={cn(
                            "px-3 py-2 rounded-full text-sm whitespace-nowrap transition",
                            sortMode === "no-roles"
                                ? "bg-gray-900 text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                        )}
                    >
                        Sans emplacement en haut
                    </button>
                </div>
            </div>

            {/* Compact list */}
            <div className="max-w-xl mx-auto p-3 space-y-2">
                {sortedStudents.map((s) => {
                    const hasComment = (s.comment || "").trim().length > 0;

                    return (
                        <button
                            key={s.id}
                            onClick={() => openDetails(s)}
                            className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex items-center justify-between gap-3 active:scale-[0.99]"
                        >
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <Dot card={s.card} />
                                    <div className="font-semibold truncate">{s.name}</div>
                                </div>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                    {s.role_front ? <Badge tone="blue">Devant</Badge> : null}
                                    {s.role_back ? <Badge tone="purple">Derrière</Badge> : null}
                                    {hasComment ? <Badge>💬</Badge> : null}
                                </div>
                            </div>

                            <div className="shrink-0 flex items-center gap-2">
                                <span className="text-gray-400">›</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Details sheet */}
            <BottomSheet
                open={!!selected}
                title={selected ? selected.name : "Détails"}
                onClose={() => setOpenId(null)}
            >
                {selected && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="text-xs font-medium text-gray-500">Carton</div>
                            <CardToggle
                                value={selected.card}
                                onChange={(c) => update(selected.id, { card: c })}
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-medium text-gray-500">
                                Emplacement
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() =>
                                        toggleRole(selected.id, "role_front", !!selected.role_front)
                                    }
                                    className={cn(
                                        "flex-1 py-2 rounded-xl border text-sm font-semibold transition active:scale-[0.98]",
                                        selected.role_front
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                    )}
                                >
                                    Devant
                                </button>
                                <button
                                    onClick={() =>
                                        toggleRole(selected.id, "role_back", !!selected.role_back)
                                    }
                                    className={cn(
                                        "flex-1 py-2 rounded-xl border text-sm font-semibold transition active:scale-[0.98]",
                                        selected.role_back
                                            ? "bg-purple-600 text-white border-purple-600"
                                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                    )}
                                >
                                    Derrière
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-medium text-gray-500">
                                Commentaire
                            </div>
                            <textarea
                                className="w-full border border-gray-200 rounded-2xl p-3 text-sm outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 min-h-[140px]"
                                placeholder="Écrire un commentaire…"
                                value={draftComment}
                                onChange={(e) => setDraftComment(e.target.value)}
                            />
                            {saving ? (
                                <div className="text-xs text-gray-500">Enregistrement…</div>
                            ) : draftComment !== savedComment ? (
                                <div className="text-xs text-gray-400">Modifications non enregistrées</div>
                            ) : (
                                <div className="text-xs text-green-600">Enregistré</div>
                            )}


                            <div className="flex gap-2">
                                <button
                                    onClick={() => setDraftComment("")}
                                    className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm"
                                >
                                    Effacer
                                </button>
                                <button
                                    onClick={saveComment}
                                    disabled={saving}
                                    className={cn(
                                        "flex-1 px-3 py-2 rounded-xl text-sm font-semibold",
                                        saving
                                            ? "bg-gray-300 text-gray-600"
                                            : "bg-gray-900 text-white hover:bg-black"
                                    )}
                                >
                                    {saving ? "Enregistrement…" : "Enregistrer"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </BottomSheet>
        </div>
    );
}
